import { observer } from "mobx-react-lite";
import { useEffect, useRef } from "react";
import { Input, Switch } from "antd";
import { HoverTooltip } from "../../BaseComps/InfoTooltip";
import { useMst } from "../../stores/main";
import { modsDefinitions } from "./mod-definitions";
import { CloseOutlined } from "@ant-design/icons";
import {
  ConnectorIcon,
  EyeIconInvisible,
} from "../../icons-and-animations/Icons";
import "./ModDetailsPanel.scss";
import "./modForms/ModForm.scss";

const { TextArea } = Input;

interface ModDetailsPanelProps {
  onClose: () => void;
}

export const ModDetailsPanel = observer(function ModDetailsPanel({
  onClose,
}: ModDetailsPanelProps) {
  const {
    modsStore: {
      mods,
      activeModId,
      updateValue,
      updateInnerModValue,
      updateModDescription,
      selectNextMod,
      selectPreviousMod,
    },
    playerStore: { allColors, assetNames, assetLayerPairs, jsonFile },
    accountStore: { isBlingsAccount },
  } = useMst();

  const panelRef = useRef<HTMLDivElement>(null);

  // Find the selected mod
  const selectedMod = mods.find((mod) => mod.id.toString() === activeModId);
  const isOpen = !!selectedMod;

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      // Only handle if focus is within the panel or on the document body
      const activeElement = document.activeElement;
      const isInputFocused =
        activeElement instanceof HTMLInputElement ||
        activeElement instanceof HTMLTextAreaElement ||
        activeElement instanceof HTMLSelectElement;

      if (isInputFocused) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        selectNextMod();
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        selectPreviousMod();
      } else if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, selectNextMod, selectPreviousMod, onClose]);

  if (!selectedMod) {
    return null;
  }

  const { moddata, id, name } = selectedMod;
  const definition = modsDefinitions[moddata.type];

  if (!definition) {
    return null;
  }

  const { ChangeFormComp, text, color } = definition;

  if (!ChangeFormComp) {
    return null;
  }

  const displayTitle = name || definition.formTitle(moddata) || text;
  const isDisabled = !!selectedMod.moddata.isDisabled;
  const toggleTitle = isDisabled ? "Enable connector" : "Disable connector";

  return (
    <div
      ref={panelRef}
      className={`mod-details-panel ${isOpen ? "is-open" : ""}${
        isDisabled ? " is-disabled" : ""
      }`}
      style={{ "--panel-color": color } as React.CSSProperties}
    >
      <div className="mod-details-panel__header">
        <div className="mod-details-panel__header-left">
          <div className="mod-details-panel__icon">
            <ConnectorIcon type={moddata.type} />
          </div>
          <div className="mod-details-panel__title-group">
            <span className="mod-details-panel__type">
              {text.toUpperCase()}
            </span>
            <span className="mod-details-panel__title" title={displayTitle}>
              {displayTitle}
            </span>
          </div>
        </div>
        <div className="mod-details-panel__actions">
          <HoverTooltip
            title={isDisabled ? "Disabled" : "Enabled"}
            description={
              isDisabled
                ? "Turn this connector back on."
                : "Temporarily stop this connector from changing the layer."
            }
            immediate
          >
            <label className="mod-details-panel__enabled-toggle">
              {isDisabled && (
                <>
                  <span className="mod-details-panel__enabled-icon">
                    <EyeIconInvisible />
                  </span>
                  <span className="mod-details-panel__enabled-text">
                    Disabled
                  </span>
                </>
              )}
              <Switch
                className="mod-details-panel__neutral-switch"
                size="small"
                checked={!isDisabled}
                onChange={(checked) => {
                  updateValue(id, "isDisabled", !checked);
                }}
                aria-label={toggleTitle}
              />
            </label>
          </HoverTooltip>
          <button
            className="mod-details-panel__close"
            onClick={onClose}
            title="Close panel (Esc)"
          >
            <CloseOutlined />
          </button>
        </div>
      </div>

      <div
        className={`mod-details-panel__content ModForm ${moddata.type}${
          isDisabled ? " disabled" : ""
        }`}
      >
        <ChangeFormComp
          key={id}
          json={jsonFile}
          index={id}
          modId={id}
          change={moddata}
          config={mods}
          assetNames={assetNames}
          allColors={allColors}
          assetLayerPairs={assetLayerPairs}
          onInnerModChange={(i, k, v) => {
            updateInnerModValue(id, k, v);
          }}
          onChange={(i, k, v) => {
            updateValue(id, k, v);
          }}
          onLayerChange={(i, assetId, layerName, layerUid, additionals) => {
            updateValue(
              id,
              ["assetId", "layerName", "layerUid", "additionals"],
              [assetId, layerName, layerUid, additionals]
            );
          }}
          handleChange={() => {}}
        />
        {isBlingsAccount && selectedMod && (
          <div className="mod-details-panel__description">
            <label>Internal Description:</label>
            <TextArea
              placeholder="Add internal description for this connector..."
              value={selectedMod.description || ""}
              onChange={(e) =>
                updateModDescription(selectedMod.id, e.target.value)
              }
              rows={2}
            />
          </div>
        )}
      </div>

      <div className="mod-details-panel__footer">
        <span className="mod-details-panel__hint">
          Use ↑↓ to navigate • Esc to close
        </span>
      </div>
    </div>
  );
});
